import { useMemo, useContext, useState } from 'react';
import { createColumnHelper, ColumnDef, Row } from '@tanstack/react-table';
import { AdminProfessionals } from '@platform/client';
import {
  AccountTypeEnum,
  AdminProExportResponse,
  AdminProfessional,
  ProfessionalApplicationStatusEnum,
} from '@platform/client/schemas';
import { AdminTable, AdminTableContext, DateCellFromString } from '../../AdminTable';
import { AdminActionsContainer, AdminListPageWrapper } from '../../AdminListPage';
import { UpdateCohortModal } from './UpdateCohortModal';
import { SingleProAppStatusModal, BatchProAppStatusModal } from './UpdateProApplicationStatusModal';
import { keepPreviousData } from '@tanstack/react-query';
import { ProUserProfileLink } from '../../components/ProfileLinks';
import { UserEmailCell } from '../../components/UserEmailCell';
import { PillBadge, Icon } from '@platform/components';
import { VerifyEmailModal } from './VerifyEmailModal';
import { DisableUserModal } from './DisableUserModal';
import { ResetUserModal } from '../../components/ResetUserModal';
import { navigate } from 'wouter/use-browser-location';
import { ADMIN_ROUTES, ROUTES } from '../../../routes/consts';

export const ProfessionalsAdmin = () => {
  return (
    <AdminListPageWrapper title={'Professionals'}>
      <ProfessionalsAdminContent />
    </AdminListPageWrapper>
  );
};

type ResetUserModalData = {
  userId: number;
  currentAccountType: AccountTypeEnum;
  currentAppStatus: ProfessionalApplicationStatusEnum | null;
};

type UpdateCohortModalData = {
  userId: number;
  currentCohort: string;
  currentPartner: string;
  proName: string;
};

type UpdateAppStatusModalData = {
  applicationId: number;
  status: ProfessionalApplicationStatusEnum;
  userId: number;
  isSapPro: boolean;
};

const columnHelper = createColumnHelper<AdminProfessional>();

const ProfessionalsAdminContent = () => {
  const { rowSelection, adminQueryOptionsV2 } = useContext(AdminTableContext);
  const [updateCohortData, setUpdateCohortData] = useState<UpdateCohortModalData>();
  const [resetUserData, setResetUserData] = useState<ResetUserModalData>();
  const [disableUserId, setDisableUserId] = useState<number>();
  const [appStatusModalData, setAppStatusModalData] = useState<UpdateAppStatusModalData>();
  const [batchVettingModalOpen, setBatchVettingModalOpen] = useState(false);
  const [emailVerifyUserId, setEmailVerifyUserId] = useState<number | undefined>(undefined);
  const { data, isSuccess, isLoading, isError } = AdminProfessionals.useGetAdminProfessionals(
    {
      ...adminQueryOptionsV2,
    },
    {
      query: {
        placeholderData: keepPreviousData,
      },
    },
  );
  const { professionals = [], filteredCount = 0 } = data || {};

  const columns = useMemo(
    () =>
      [
        columnHelper.display({
          id: 'selected',
          header: ({ table }) => (
            <div className='flex flex-col items-center gap-3'>
              <label>Select</label>
              <input
                type='checkbox'
                className='h-6 w-6'
                onChange={table.getToggleAllRowsSelectedHandler()}
                checked={table.getIsAllRowsSelected()}
              />
            </div>
          ),
          cell: ({ row }) => (
            <div className='flex items-center justify-center'>
              <input
                type='checkbox'
                className='h-4 w-4'
                onChange={row.getToggleSelectedHandler()}
                checked={row.getIsSelected()}
                aria-label='Select Row'
                disabled={row.original.applicationId == null}
              />
            </div>
          ),
          size: 25,
        }),
        columnHelper.accessor('id', {
          header: 'Id',
          cell: (id) => id.getValue(),
          meta: {
            filterVariant: 'text',
          },
          size: 25,
        }),
        columnHelper.accessor('name', {
          header: 'Name',
          cell: ({ row }) => {
            return <ProUserProfileLink userId={row.original.id} name={row.original.name} />;
          },
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('email', {
          header: 'Email',
          cell: ({ row }) => (
            <UserEmailCell
              email={row.original.email}
              emailVerified={row.original.emailVerified}
              userId={row.original.id}
              setEmailVerifyUserId={setEmailVerifyUserId}
            />
          ),
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('skills', {
          header: 'Skills',
          cell: (skills) => {
            return skills.getValue().map((skill: string) => (
              <PillBadge variant='plain' color='indigo' key={skill} size='sm'>
                {skill}
              </PillBadge>
            ));
          },
          meta: {
            filterVariant: 'text',
          },
          enableSorting: false,
        }),
        columnHelper.accessor('clientName', {
          header: 'Corporate',
          cell: (clientName) => {
            return <span>{clientName.getValue()}</span>;
          },
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('cohortName', {
          header: 'Cohort',
          cell: ({ row }) => {
            return (
              <div className='flex justify-between'>
                <span>{row.original.cohortName}</span>
                {row.original.cohortName && (
                  <button
                    onClick={() =>
                      setUpdateCohortData({
                        userId: row.original.id,
                        currentCohort: row.original.cohortName ?? '',
                        currentPartner: row.original.clientName ?? '',
                        proName: row.original.name,
                      })
                    }
                  >
                    <Icon type='edit-01' size='14' />
                  </button>
                )}
              </div>
            );
          },
          meta: {
            filterVariant: 'text',
          },
        }),
        columnHelper.accessor('applyCompleted', {
          header: 'Apply Complete',
          cell: ({ row }) => {
            return <span>{row.original.applyCompleted ? 'Yes' : 'No'}</span>;
          },
          meta: {
            filterVariant: 'text',
          },
          enableSorting: false,
        }),
        columnHelper.accessor('applicationStatus', {
          header: 'Apply Status',
          cell: ({ row }) => {
            // We do not currently have any functionality around rejecting/waitlisting what was formerly transform pro (now individual)
            // so for now we just hide the edit button
            return row.original.applicationStatus ?
                <div className='flex justify-between'>
                  <ProAppStatusBadge status={row.original.applicationStatus} />
                  {row.original.clientName !== 'Ernst & Young' && (
                    <button
                      onClick={() => {
                        setAppStatusModalData({
                          applicationId: row.original.applicationId as number,
                          status: row.original
                            .applicationStatus as ProfessionalApplicationStatusEnum,
                          userId: row.original.id as number,
                          isSapPro: row.original.clientName === 'SAP',
                        });
                      }}
                    >
                      <Icon type='edit-01' size='14' />
                    </button>
                  )}
                </div>
              : <div className='text-gray-500'>N/A</div>;
          },
          meta: {
            filterVariant: 'text',
          },
          enableSorting: false,
        }),
        columnHelper.accessor('createdAt', {
          header: 'Created At',
          cell: (createdAt) => {
            return <DateCellFromString value={createdAt.getValue()} />;
          },
        }),
        columnHelper.display({
          header: 'Actions',
          cell: ({ row }) => {
            return (
              <ProfessionalsActions
                row={row}
                userId={row.original.id}
                setDisableUserId={setDisableUserId}
                isDisabled={row.original.isDisabled}
                setResetUserData={setResetUserData}
              />
            );
          },
        }),
      ] as ColumnDef<AdminProfessional>[],
    [],
  );

  return (
    <>
      <AdminTable
        columns={columns}
        data={professionals}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        filteredCount={filteredCount}
        enableFiltering={true}
        enableMultiRowSelection={true}
        getRowId={(row) => String(row.id)}
        tableActions={[
          {
            text: 'Batch vetting',
            callback: () => setBatchVettingModalOpen(true),
            isDisabled: () => Object.keys(rowSelection ?? {}).length === 0,
          },
          {
            text: 'Search',
            callback: () => {
              navigate(`platform${ROUTES.ADMIN}${ADMIN_ROUTES.SEARCH_PROFESSIONALS}`);
            },
          },
        ]}
        exportOptions={{
          exportQuery: AdminProfessionals.useGetUserExportData,
          dataToExportFn: (data: AdminProExportResponse) => data.professionals,
          exportFileName: `pro_user_export_${new Date().toISOString().replace(' ', '_')}.csv`,
        }}
      />
      {updateCohortData && (
        <UpdateCohortModal
          userId={updateCohortData.userId}
          currentCohort={updateCohortData.currentCohort}
          currentPartner={updateCohortData.currentPartner}
          onClose={() => setUpdateCohortData(undefined)}
          proName={updateCohortData.proName}
          queryKeyToInvalidate={AdminProfessionals.getGetAdminProfessionalsQueryKey({
            ...adminQueryOptionsV2,
          })}
        />
      )}
      {emailVerifyUserId && (
        <VerifyEmailModal
          userId={emailVerifyUserId}
          onClose={() => setEmailVerifyUserId(undefined)}
        />
      )}
      {appStatusModalData && (
        <SingleProAppStatusModal
          onClose={() => setAppStatusModalData(undefined)}
          applicationId={appStatusModalData.applicationId}
          status={appStatusModalData.status}
          userId={appStatusModalData.userId}
          isSapPro={appStatusModalData.isSapPro}
          queryKeyToInvalidate={AdminProfessionals.getGetAdminProfessionalsQueryKey({
            ...adminQueryOptionsV2,
          })}
        />
      )}
      {batchVettingModalOpen && (
        <BatchProAppStatusModal
          onClose={() => setBatchVettingModalOpen(false)}
          queryKeyToInvalidate={AdminProfessionals.getGetAdminProfessionalsQueryKey({
            ...adminQueryOptionsV2,
          })}
        />
      )}
      {disableUserId && (
        <DisableUserModal userId={disableUserId} onClose={() => setDisableUserId(undefined)} />
      )}
      {resetUserData && (
        <ResetUserModal
          userId={resetUserData.userId}
          currentAccountType={resetUserData.currentAccountType}
          currentAppStatus={resetUserData.currentAppStatus}
          onClose={() => setResetUserData(undefined)}
        />
      )}
    </>
  );
};

export const getProBadgeColor = (status: ProfessionalApplicationStatusEnum) => {
  switch (status) {
    case ProfessionalApplicationStatusEnum.approved:
      return 'success-500';
    case ProfessionalApplicationStatusEnum.rejected:
      return 'orange';
    case ProfessionalApplicationStatusEnum.pending:
      return 'warning';
    case ProfessionalApplicationStatusEnum.waitlisted:
      return 'warning';
    default:
      throw new Error(`Unknown application status: ${status}`);
  }
};

const ProAppStatusBadge = (props: { status: ProfessionalApplicationStatusEnum }) => {
  const { status } = props;
  const badgeColor = getProBadgeColor(status);
  return (
    <PillBadge variant='plain' color={badgeColor} size='sm'>
      {status.split('_').join(' ').toUpperCase()}
    </PillBadge>
  );
};

const ProfessionalsActions = (props: {
  row: Row<AdminProfessional>;
  userId: number;
  setDisableUserId: (id: number) => void;
  isDisabled: boolean;
  setResetUserData: (data: ResetUserModalData) => void;
}) => {
  const { userId } = props;
  return (
    <AdminActionsContainer
      actions={[
        { text: 'Review', type: 'link', href: `/review/${userId}` },
        {
          text: 'Reset',
          type: 'button',
          onPress: () =>
            props.setResetUserData({
              userId: props.row.original.id,
              currentAccountType: AccountTypeEnum.experteer,
              currentAppStatus: props.row.original.applicationStatus,
            }),
        },
        {
          text: props.isDisabled ? 'Disabled' : 'Disable',
          type: 'button',
          onPress: () => props.setDisableUserId(userId),
          isDisabled: props.isDisabled,
          linkColor: 'red',
        },
      ]}
    />
  );
};
